import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Paper, Typography, Chip, CircularProgress, Grid, Button,
  Table, TableBody, TableRow, TableCell, TableHead, Avatar, alpha
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GroupIcon from '@mui/icons-material/Group';
import { searchParties, getPartyMembers } from '../services/api';
import { getParliamentarianPhotoUrl } from '../utils/photos';
import CopyLinkButton from './CopyLinkButton';
import ShareButton from './ShareButton';

// Canonical sentinel used across the Handbook records-of-service feed for
// "open / no value". An open-ended tenure (the parliamentarian is still a
// current member of this party) has DateEnd1 === '1900-01-01'. Per ali-pm's
// probe, Hanson herself sits on that sentinel.
const OPEN_SENTINEL = '1900-01-01';

// Issue #277: the bare DateEnd1==sentinel predicate inflated the Liberal
// current-members count to 453 (vs reality ~30-40). Many old ROS-11 rows
// have an open-sentinel DateEnd1 even though the parliamentarian left
// decades ago. Prefer the server-derived flag (which AND's
// InCurrentParliament with an open-ended DateEnd1); fall back to the old
// predicate only when the server response predates the fix (rolling deploy).
const isCurrentTenure = (m) =>
  m._is_current_derived === true
  || (m._is_current_derived === undefined
      && String(m.DateEnd1 || '').slice(0, 10) === OPEN_SENTINEL);
const isSentinelStart = (m) => String(m.DateStart1 || '').slice(0, 10) === OPEN_SENTINEL;

// ROS-11 dates are already ISO-date strings (`YYYY-MM-DD`). The spec asks for
// the raw ISO form to be rendered as-is, suppressing the 1900-01-01 sentinel
// on the start column.
const formatIsoDate = (s) => {
  if (!s) return null;
  const v = String(s).slice(0, 10);
  if (v === OPEN_SENTINEL) return null;
  return v;
};

export default function PartyDetail() {
  const { pid } = useParams();
  const [party, setParty] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const partyData = await searchParties(`pID:${pid}`, 1, 1);
        if (partyData.hits && partyData.hits.length > 0) {
          const p = partyData.hits[0];
          setParty(p);
          try {
            const memberData = await getPartyMembers(pid);
            setMembers(Array.isArray(memberData.hits) ? memberData.hits : []);
          } catch (memberErr) {
            console.error('Error fetching members:', memberErr);
            setMembers([]);
          }
        } else {
          setError('Party not found');
        }
      } catch (err) {
        console.error('Error fetching party:', err);
        setError('Failed to load party data');
      }
      setLoading(false);
    };
    fetchData();
  }, [pid]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !party) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="error">{error || 'Party not found'}</Typography>
        <Button component={RouterLink} to="/parties" startIcon={<ArrowBackIcon />} sx={{ mt: 2 }}>
          Back to Parties
        </Button>
      </Box>
    );
  }

  // Partition + sort: current first (DateStart1 desc within, sentinel-starts
  // sink to bottom of the current bucket), then previous by DateEnd1 desc.
  const current = [];
  const previous = [];
  members.forEach((m) => {
    if (isCurrentTenure(m)) current.push(m);
    else previous.push(m);
  });
  const dateKey = (s) => String(s || '').slice(0, 10);
  current.sort((a, b) => {
    const ak = isSentinelStart(a) ? '' : dateKey(a.DateStart1);
    const bk = isSentinelStart(b) ? '' : dateKey(b.DateStart1);
    return bk.localeCompare(ak);
  });
  previous.sort((a, b) => dateKey(b.DateEnd1).localeCompare(dateKey(a.DateEnd1)));
  const sortedMembers = [...current, ...previous];

  const currentCount = current.length;
  const previousCount = previous.length;

  // A party is active if it has any open-ended (current) tenure.
  const isActive = party.Active === true || party.Active === 'true' || currentCount > 0;
  const partyColor = party.Colour || '#ad1457';

  const renderChamber = (m) => {
    // Backend enriches each ROS-11 hit with `_chamber_derived` (a list,
    // since one PHID can serve as both Member and Senator) sourced from
    // the parliamentarians index. Fall back to the original null-prone
    // ROS-11 `MpOrSenator` field for older API responses.
    const derived = m._chamber_derived;
    if (Array.isArray(derived) && derived.length > 0) {
      return derived.join(' / ');
    }
    if (m.MpOrSenator) return m.MpOrSenator;
    return '—';
  };

  return (
    <Box>
      {/* Navigation bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button
          component={RouterLink}
          to="/parties"
          startIcon={<ArrowBackIcon />}
          sx={{ color: 'text.secondary' }}
        >
          Back to Parties
        </Button>
        <CopyLinkButton size="small" />
        <ShareButton size="small" />
      </Box>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12}>
          {/* Hero Section */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(partyColor, 0.08)} 0%, ${alpha(partyColor, 0.02)} 100%)`,
              border: '1px solid',
              borderColor: alpha(partyColor, 0.15)
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  bgcolor: partyColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '1.5rem',
                  border: '3px solid white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }}
              >
                {party.Abbrev?.substring(0, 3) || party.Name?.charAt(0)}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: partyColor,
                    mb: 0.5
                  }}
                >
                  {party.Name}
                </Typography>

                {/* Abbreviation subtitle */}
                {party.Abbrev && (
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontWeight: 400 }}>
                    {party.Abbrev}
                  </Typography>
                )}

                {/* Status chips */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {isActive ? (
                    <Chip
                      label="Active"
                      size="small"
                      color="success"
                    />
                  ) : (
                    <Chip
                      label="Inactive"
                      size="small"
                      color="default"
                      variant="outlined"
                    />
                  )}
                  <Chip
                    label={`${members.length} Member${members.length !== 1 ? 's' : ''} (All time)`}
                    size="small"
                    variant="outlined"
                  />
                  {currentCount > 0 && (
                    <Chip
                      label={`${currentCount} Current`}
                      size="small"
                      sx={{ bgcolor: alpha(partyColor, 0.1), color: partyColor, fontWeight: 500 }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Paper>

          <Grid container spacing={3}>
            {/* Party Details */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Party Details
                </Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500, color: 'text.secondary', width: '40%' }}>Party ID</TableCell>
                      <TableCell>{party.pID}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>Full Name</TableCell>
                      <TableCell>{party.Name}</TableCell>
                    </TableRow>
                    {party.Abbrev && (
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>Abbreviation</TableCell>
                        <TableCell>{party.Abbrev}</TableCell>
                      </TableRow>
                    )}
                    {party.Colour && (
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>Party Colour</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                bgcolor: party.Colour,
                                border: '1px solid #ccc'
                              }}
                            />
                            {party.Colour}
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>Status</TableCell>
                      <TableCell>{isActive ? 'Active' : 'Inactive'}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Paper>
            </Grid>

            {/* Statistics */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Statistics
                </Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500, color: 'text.secondary', width: '40%' }}>Total Members (All Time)</TableCell>
                      <TableCell>{members.length}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>Current Members</TableCell>
                      <TableCell>{currentCount}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>Previous Members</TableCell>
                      <TableCell>{previousCount}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Paper>
            </Grid>

            {/* Members List — current + previous tenures in one table.
                Source: recordsofservice ROSTypeID=11 (canonical party-membership
                history; captures floor-crossers + historic members the
                parliamentarians.Party field omits). */}
            {sortedMembers.length > 0 && (
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GroupIcon color="action" />
                    Party Members ({sortedMembers.length})
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {currentCount} current, {previousCount} previous
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Chamber</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Start</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>End</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedMembers.map((member, idx) => {
                        const current = isCurrentTenure(member);
                        const displayName = member.DisplayName || 'Unknown';
                        // Backend-enriched start date: prefers ROS-11
                        // DateStart1 when not the 1900 sentinel, else falls
                        // back to parliamentarians.ServiceHistory_Start.
                        const startDate = member._start_date_derived
                          || formatIsoDate(member.DateStart1)
                          || '—';
                        return (
                          <TableRow key={`${member.PHID || 'na'}-${idx}`}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Avatar
                                  src={member.PHID ? getParliamentarianPhotoUrl(member.PHID) : undefined}
                                  alt={displayName}
                                  sx={{ width: 36, height: 36, bgcolor: alpha(partyColor, 0.15), color: partyColor, fontSize: '0.85rem' }}
                                >
                                  {displayName.split(',')[0]?.charAt(0) || '?'}
                                </Avatar>
                                {member.PHID ? (
                                  <Button
                                    component={RouterLink}
                                    to={`/parliamentarians/${member.PHID}`}
                                    size="small"
                                    sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                                  >
                                    {displayName}
                                  </Button>
                                ) : (
                                  <Typography variant="body2">{displayName}</Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{renderChamber(member)}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {startDate}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {current ? (
                                <Chip label="Current" size="small" color="success" />
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  {formatIsoDate(member.DateEnd1) || '—'}
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}
